import { useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import {
  AgxButton,
  AgxColumn,
  AgxModal,
  AgxRow,
  AgxSegmentPicker,
  AgxTypography,
  Icon_AlertCircle,
} from '@urbanx/agx.ui-components2'
import {
  CampaignDetailModelState,
  CoolingOffDetailsCoolingOffType,
  Deposit,
  DepositCondition,
  DepositConditionType,
  DepositPayable,
  DepositType,
  DepositValueType,
  FormConditionCondition,
  FormConditionConditionType,
  useStaffGetContractDates,
  useStaffGetSaleInformation,
} from '@urbanx/agx.campaigns.client'
import { FormConditionOnDate } from '../components/FormConditionOnDate'
import FormConditionNDays from '../components/FormConditionNDays'
import { useSelectedCampaign, useSelectedContract } from 'hooks'
import { QldDepositOptionsComponent } from './QldDepositOptions'
import {
  NswDepositOptionsComponent,
  NswDepositPayableOption,
  NswDepositSelection,
} from './NswDepositOptions'

interface AddDepositButtonProps {
  id: string
  label: string
  depositType: DepositType
  onAddDeposits: (
    initialDeposit: Deposit,
    balanceDeposit: Deposit | undefined
  ) => void
  disabled?: boolean
}

const SegmentOptions = [
  { title: 'on...', value: DepositPayable.On },
  { title: '# days from...', value: DepositPayable.NDaysFrom },
]

export function AddDepositButton({
  id,
  label,
  depositType,
  onAddDeposits,
  disabled,
}: AddDepositButtonProps) {
  const { campaign } = useSelectedCampaign()
  const { contract } = useSelectedContract()
  const isNsw = campaign?.state === CampaignDetailModelState.NSW
  const isQld = campaign?.state === CampaignDetailModelState.QLD

  const { data: contractDates } = useStaffGetContractDates(
    contract?.contractId || '',
    !!contract?.contractId && isNsw
  )

  const { data: saleInformation } = useStaffGetSaleInformation(
    contract?.contractId || '',
    !!contract?.contractId && isNsw
  )

  const nswCoolingOffDate = useMemo(() => {
    if (!isNsw || !contractDates?.contractDate) return undefined

    const coolingOffDetails = contractDates.coolingOffDetails
    if (
      coolingOffDetails?.coolingOffType ===
      CoolingOffDetailsCoolingOffType.FiveDays
    ) {
      return dayjs(contractDates.contractDate).add(5, 'day').toDate()
    } else if (
      coolingOffDetails?.coolingOffType ===
        CoolingOffDetailsCoolingOffType.NDays &&
      coolingOffDetails?.coolingOffDays
    ) {
      return dayjs(contractDates.contractDate)
        .add(coolingOffDetails.coolingOffDays, 'day')
        .toDate()
    }
    // Default to 5 days if no cooling off details set yet
    return dayjs(contractDates.contractDate).add(5, 'day').toDate()
  }, [isNsw, contractDates])

  const [openDepositModal, setOpenDepositModal] = useState<boolean>(false)
  const [deposit, setDeposit] = useState<Deposit | undefined>(undefined)
  const [depositValue, setDepositValue] = useState<DepositValueType>(
    DepositValueType.Dollar
  )
  const [selectedSegment, setSelectedSegment] = useState<string>(
    DepositPayable.NDaysFrom
  )
  const [internalValidate, setInternalValidate] = useState<boolean>(false)
  const [internalErrorMessage, setInternalErrorMessage] = useState<
    string | undefined
  >(undefined)
  const [nswSelection, setNswSelection] = useState<
    NswDepositSelection | undefined
  >(undefined)

  const isNswFullUpfront =
    isNsw &&
    nswSelection?.depositPayableOption ===
      NswDepositPayableOption.FullDepositUpfront

  useEffect(() => {
    setInternalValidate(false)
  }, [deposit])

  useEffect(() => {
    setInternalErrorMessage(undefined)
    setDeposit(undefined)
    setInternalValidate(false)
    setNswSelection(undefined)
  }, [openDepositModal])

  useEffect(() => {
    setInternalErrorMessage(undefined)
    setInternalValidate(false)
  }, [nswSelection])

  const onSelectedSegmentChange = useCallback((segment: string) => {
    setSelectedSegment(segment)
    setDeposit((prev) => ({
      ...prev,
      depositPayable: segment as DepositPayable,
    }))
  }, [])

  const onConditionValueChange = useCallback(
    (updatedValue: Deposit | unknown) => {
      const conditionUpdate = updatedValue as Deposit
      setDeposit((prev) => ({
        ...prev,
        condition: conditionUpdate.condition,
        conditionType: conditionUpdate.conditionType,
        referenceDate: conditionUpdate.referenceDate,
        daysFromDate: conditionUpdate.daysFromDate,
        specialConditionId: conditionUpdate.specialConditionId,
        depositPayable: conditionUpdate.depositPayable,
      }))
    },
    []
  )

  const onConfirmClick = useCallback(() => {
    setInternalValidate(true)

    if (isNsw && nswSelection) {
      const hasAmount =
        nswSelection.depositValueType === DepositValueType.Percentage
          ? nswSelection.percent !== undefined && nswSelection.percent > 0
          : nswSelection.amount !== undefined && nswSelection.amount > 0

      if (!hasAmount) {
        setInternalErrorMessage('Enter a deposit amount')
        return
      }

      const salePrice = saleInformation?.salePrice || 0
      const quarterPercentAmount = salePrice * 0.0025

      if (!isNswFullUpfront) {
        if (!salePrice || salePrice <= 0) {
          setInternalErrorMessage(
            'Sale price is required to calculate 0.25% initial deposit'
          )
          return
        }

        if (nswSelection.depositValueType === DepositValueType.Dollar) {
          // Dollar-based: total deposit must be at least 0.25% of sale price
          const minimumAmount = Math.ceil(quarterPercentAmount * 100) / 100
          if (
            nswSelection.amount !== undefined &&
            nswSelection.amount < minimumAmount
          ) {
            setInternalErrorMessage(
              `Minimum deposit amount is $${minimumAmount.toFixed(2)} (0.25% of sale price)`
            )
            return
          }
        } else {
          // Percentage-based: total deposit must be at least 0.25%
          if (
            nswSelection.percent !== undefined &&
            nswSelection.percent < 0.25
          ) {
            setInternalErrorMessage('Minimum deposit percentage is 0.25%')
            return
          }
        }
      }

      // Create initial deposit (0.25% or full deposit)
      // For full upfront, the deposit is due on contract date
      // For 0.25% upfront, the initial deposit is 0.25% of sale price
      let initialDeposit: Deposit

      if (isNswFullUpfront) {
        // Full deposit upfront - use the selected type and value
        initialDeposit = {
          depositType: DepositType.Initial,
          depositValueType: nswSelection.depositValueType,
          percent: nswSelection.percent,
          amount: nswSelection.amount,
          condition: DepositCondition.OnDate,
          conditionType: DepositConditionType.Contract,
          isDepositSelected: true,
        }
      } else {
        // 0.25% upfront - initial is always 0.25%
        initialDeposit = {
          depositType: DepositType.Initial,
          depositValueType: DepositValueType.Percentage,
          percent: 0.25,
          amount: undefined,
          condition: DepositCondition.OnDate,
          conditionType: DepositConditionType.Contract,
          isDepositSelected: true,
        }
      }

      // Create balance deposit only if 0.25% upfront is selected
      // Balance deposit is due on the cooling off end date
      // Balance is the total deposit minus the 0.25% initial deposit
      let balanceDeposit: Deposit | undefined
      if (!isNswFullUpfront) {
        if (nswSelection.depositValueType === DepositValueType.Percentage) {
          // Percentage-based: balance is the percentage minus 0.25%
          const balancePercent =
            nswSelection.percent !== undefined
              ? nswSelection.percent - 0.25
              : undefined
          balanceDeposit = {
            depositType: DepositType.Balance,
            depositValueType: DepositValueType.Percentage,
            percent: balancePercent,
            amount: undefined,
            condition: DepositCondition.OnDate,
            conditionType: DepositConditionType.Contract,
            referenceDate: nswCoolingOffDate,
            isDepositSelected: true,
          }
        } else {
          // Dollar-based: balance is the dollar amount minus 0.25% of sale price
          const balanceAmount =
            nswSelection.amount !== undefined
              ? nswSelection.amount - quarterPercentAmount
              : undefined
          // Only create balance deposit if balance is greater than $0
          if (balanceAmount && balanceAmount > 0) {
            balanceDeposit = {
              depositType: DepositType.Balance,
              depositValueType: DepositValueType.Dollar,
              percent: undefined,
              amount: balanceAmount,
              condition: DepositCondition.OnDate,
              conditionType: DepositConditionType.Contract,
              referenceDate: nswCoolingOffDate,
              isDepositSelected: true,
            }
          }
        }
      }

      if (onAddDeposits) {
        onAddDeposits(initialDeposit, balanceDeposit)
      }

      setOpenDepositModal(false)
      setInternalValidate(false)
      setInternalErrorMessage(undefined)
      return
    }

    if (!deposit) {
      setInternalErrorMessage('Enter a deposit amount')
      return
    }

    const hasAmount =
      deposit.depositValueType === DepositValueType.Percentage
        ? deposit.percent !== undefined && deposit.percent > 0
        : deposit.amount !== undefined && deposit.amount > 0

    if (!hasAmount) {
      setInternalErrorMessage('Enter a deposit amount')
      return
    }

    if (!deposit.condition) {
      setInternalErrorMessage('Select a condition')
      return
    }

    const conditionStr = deposit.condition as string

    if (conditionStr === 'OnSpecificDate' && !deposit.referenceDate) {
      setInternalErrorMessage('Select a date')
      return
    } else if (conditionStr === 'NdaysFromDate' && !deposit.daysFromDate) {
      setInternalErrorMessage('Enter number of days')
      return
    } else if (
      conditionStr === 'UponSatisfactionOfSpecialCondition' &&
      !deposit.specialConditionId
    ) {
      setInternalErrorMessage('Enter special condition clause')
      return
    }

    onAddDeposits(
      depositType === DepositType.Initial
        ? {
            ...deposit,
            depositType: DepositType.Initial,
            isDepositSelected: true,
          }
        : {},
      depositType === DepositType.Balance
        ? {
            ...deposit,
            depositType: DepositType.Balance,
            isDepositSelected: true,
          }
        : undefined
    )
    setOpenDepositModal(false)
    setInternalValidate(false)
    setInternalErrorMessage(undefined)
  }, [
    deposit,
    onAddDeposits,
    depositType,
    isNsw,
    isNswFullUpfront,
    nswSelection,
    nswCoolingOffDate,
    saleInformation,
  ])

  return (
    <>
      <AgxButton
        id={`${id}-add-deposit`}
        variant="hollow"
        text={label}
        disabled={disabled}
        onClick={() => setOpenDepositModal(true)}
      />
      {openDepositModal && (
        <AgxModal
          title={label}
          variant="withDivider"
          type="single"
          actionButtonContainerStyle="withDivider"
          setIsDialogOpen={setOpenDepositModal}
          content={
            <AgxColumn
              gap="spacing-lg"
              padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
            >
              <AgxColumn id={id} gap="spacing-md">
                {isQld && (
                  <QldDepositOptionsComponent
                    id={id}
                    depositValue={depositValue}
                    onDepositValueChange={setDepositValue}
                    onDepositChange={setDeposit}
                  />
                )}
                {isNsw && (
                  <NswDepositOptionsComponent
                    id={id}
                    onSelectionChange={setNswSelection}
                  />
                )}
                {!isNsw && (
                  <AgxColumn gap="spacing-md" fill>
                    <AgxSegmentPicker
                      id={`${id}-depositPayableSegment`}
                      segments={SegmentOptions}
                      onChange={onSelectedSegmentChange}
                    />
                    {selectedSegment === DepositPayable.On && (
                      <FormConditionOnDate
                        id={id}
                        onSpecificDateOptions={
                          depositType === DepositType.Initial
                            ? [
                                FormConditionCondition.OnDate,
                                FormConditionCondition.OnSpecificDate,
                                FormConditionCondition.FinanceBeingSatisfied,
                                FormConditionCondition.BuildingAndPestBeingSatisfied,
                                FormConditionCondition.UponSatisfactionOfSpecialCondition,
                              ]
                            : [
                                FormConditionCondition.NdaysFromDate,
                                FormConditionCondition.OnSpecificDate,
                                FormConditionCondition.FinanceBeingSatisfied,
                                FormConditionCondition.BuildingAndPestBeingSatisfied,
                                FormConditionCondition.UponSatisfactionOfSpecialCondition,
                              ]
                        }
                        defaultValue={deposit}
                        onValueChange={onConditionValueChange}
                        conditionTypeOverride={
                          depositType === DepositType.Initial
                            ? FormConditionConditionType.Contract
                            : undefined
                        }
                        depositType={depositType}
                      />
                    )}
                    {selectedSegment === DepositPayable.NDaysFrom && (
                      <FormConditionNDays
                        id={id}
                        fromDateOptions={[
                          FormConditionCondition.NdaysFromDate,
                          FormConditionCondition.FinanceBeingSatisfied,
                          FormConditionCondition.BuildingAndPestBeingSatisfied,
                          FormConditionCondition.UponSatisfactionOfSpecialCondition,
                        ]}
                        defaultValue={deposit}
                        hasPriceOrPercentage={false}
                        onValueChange={onConditionValueChange}
                        validate={internalValidate}
                        conditionTypeOverride={
                          depositType === DepositType.Initial
                            ? FormConditionConditionType.Contract
                            : undefined
                        }
                        depositType={depositType}
                      />
                    )}
                  </AgxColumn>
                )}
              </AgxColumn>
              {internalValidate && internalErrorMessage && (
                <div className={'errorMessage'}>
                  <Icon_AlertCircle type="danger" />
                  <AgxTypography
                    id={`${id}-error`}
                    type="form_error"
                    size="small"
                  >
                    {internalErrorMessage}
                  </AgxTypography>
                </div>
              )}
            </AgxColumn>
          }
          actionButtons={
            <AgxRow gap="spacing-md" alignItems="flex-end" fill>
              <AgxButton
                id={`${id}-cancel-add-deposit`}
                variant="hollow"
                text="Cancel"
                size="large"
                stretch
                onClick={() => setOpenDepositModal(false)}
              />
              <AgxButton
                id={`${id}-save-add-deposit`}
                variant="primary"
                text="Save"
                size="large"
                stretch
                onClick={onConfirmClick}
                disabled={internalValidate && !!internalErrorMessage}
              />
            </AgxRow>
          }
        />
      )}
    </>
  )
}
